/**
 * Polish working days calculator.
 * Excludes: weekends + Polish public holidays (fixed + Easter-based movable ones).
 */

function easterDate(year: number): Date {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}

function addDays(d: Date, n: number): Date {
  return new Date(d.getTime() + n * 86_400_000);
}

function fmt(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function getPolishHolidays(year: number): Set<string> {
  const easter = easterDate(year);
  const days = [
    `${year}-01-01`, // Nowy Rok
    `${year}-01-06`, // Trzech Króli
    fmt(easter),                  // Wielkanoc
    fmt(addDays(easter, 1)),      // Poniedziałek Wielkanocny
    `${year}-05-01`, // Święto Pracy
    `${year}-05-03`, // Święto Konstytucji 3 Maja
    fmt(addDays(easter, 49)),     // Zielone Świątki
    fmt(addDays(easter, 60)),     // Boże Ciało
    `${year}-08-15`, // Wniebowzięcie NMP
    `${year}-11-01`, // Wszystkich Świętych
    `${year}-11-11`, // Święto Niepodległości
    `${year}-12-25`, // Boże Narodzenie
    `${year}-12-26`, // Drugi dzień Bożego Narodzenia
  ];
  if (year >= 2025) days.push(`${year}-12-24`); // Wigilia (od 2025)
  return new Set(days);
}

/** Expand company holiday date ranges into a set of ISO date strings. */
export function buildCompanyHolidaySet(
  holidays: Array<{ start_date: string; end_date: string }>,
): Set<string> {
  const set = new Set<string>();
  for (const ch of holidays) {
    const [sy, sm, sd] = ch.start_date.split("-").map(Number);
    const [ey, em, ed] = ch.end_date.split("-").map(Number);
    const cur = new Date(sy, sm - 1, sd);
    const end = new Date(ey, em - 1, ed);
    while (cur <= end) {
      set.add(fmt(cur));
      cur.setDate(cur.getDate() + 1);
    }
  }
  return set;
}

/**
 * Returns the ISO date string of the nth Polish working day in the given month,
 * or null if n exceeds the number of working days in that month.
 */
export function getNthWorkingDay(year: number, month: number, n: number): string | null {
  const holidays = getPolishHolidays(year);
  let count = 0;
  const cur = new Date(year, month - 1, 1);
  while (cur.getMonth() === month - 1) {
    const dow = cur.getDay();
    if (dow !== 0 && dow !== 6 && !holidays.has(fmt(cur))) {
      count++;
      if (count === n) return fmt(cur);
    }
    cur.setDate(cur.getDate() + 1);
  }
  return null;
}

/**
 * Count Polish working days between start and end (both inclusive).
 * @param start         ISO date string "YYYY-MM-DD"
 * @param end           ISO date string "YYYY-MM-DD"
 * @param extraHolidays Additional dates to treat as non-working (e.g. company holidays)
 */
export function countWorkingDays(start: string, end: string, extraHolidays?: Set<string>): number {
  if (!start || !end) return 0;
  const [sy, sm, sd] = start.split("-").map(Number);
  const [ey, em, ed] = end.split("-").map(Number);
  const startDate = new Date(sy, sm - 1, sd);
  const endDate = new Date(ey, em - 1, ed);
  if (endDate < startDate) return 0;

  const holidays = new Set<string>();
  for (let y = startDate.getFullYear(); y <= endDate.getFullYear(); y++) {
    getPolishHolidays(y).forEach((h) => holidays.add(h));
  }
  if (extraHolidays) extraHolidays.forEach((h) => holidays.add(h));

  let count = 0;
  const cur = new Date(startDate);
  while (cur <= endDate) {
    const dow = cur.getDay();
    if (dow !== 0 && dow !== 6 && !holidays.has(fmt(cur))) count++;
    cur.setDate(cur.getDate() + 1);
  }
  return count;
}
